;(() => {
  const namespace = (globalThis.StylistLocator = globalThis.StylistLocator || {})

  // ============================================================================
  // Dependencies
  // ============================================================================

  const { utils = {} } = namespace
  const { normalizeStringField = (v) => v, parseCoordinate = (v) => v } = utils

  const DEFAULT_ROLE = 'IBE® Stylist'
  const MIN_SEARCH_LENGTH = 2
  const AUTOCOMPLETE_RESULT_LIMIT = 3
  const CERTIFIED_SALON_TOKENS = ['certified salon']

  function normalizeName(firstName, lastName) {
    const safeFirst = normalizeStringField(firstName) || ''
    const safeLast = normalizeStringField(lastName) || ''
    return `${safeFirst} ${safeLast}`.trim()
  }

  function normalizeTagList(tags) {
    if (!Array.isArray(tags)) return []
    return tags.map((tag) => normalizeStringField(tag)).filter(Boolean)
  }

  function hasCertifiedSalonTag(tags) {
    return normalizeTagList(tags).some((tag) => {
      const lowerTag = tag.toLowerCase()
      return CERTIFIED_SALON_TOKENS.some((token) => lowerTag.includes(token))
    })
  }

  function isCertifiedSalonCustomer(customer, role, tags) {
    const roleLower = (role || '').toLowerCase()
    const roleCategory = normalizeStringField(customer.role_category || customer.roleCategory)
    const roleCategoryLower = (roleCategory || '').toLowerCase()

    return (
      customer.is_salon_owner === true ||
      customer.certified_salon === true ||
      roleLower === 'certified salon' ||
      roleCategoryLower === 'certified_salon' ||
      hasCertifiedSalonTag(tags)
    )
  }

  function shouldIncludeInSearchResult(stylist) {
    return true
  }

  function withRoleCategory(stylistRecord) {
    const { geometry = {} } = namespace
    return Object.assign(stylistRecord, {
      roleCategory: geometry.determineRoleCategory
        ? geometry.determineRoleCategory(stylistRecord)
        : 'default',
    })
  }

  function mapCustomerBase(customer) {
    const name = normalizeName(customer.first_name, customer.last_name)
    const role = normalizeStringField(customer.role) || DEFAULT_ROLE
    const tags = normalizeTagList(customer.tags)
    const isCertifiedSalon = isCertifiedSalonCustomer(customer, role, tags)

    return {
      name,
      email: normalizeStringField(customer.email) || '',
      description: normalizeStringField(customer.description),
      latitude: parseCoordinate(customer.latitude),
      longitude: parseCoordinate(customer.longitude),
      profile_photo: normalizeStringField(customer.profile_picture),
      image_1: normalizeStringField(customer.image_1),
      image_2: normalizeStringField(customer.image_2),
      image_3: normalizeStringField(customer.image_3),
      image_4: normalizeStringField(customer.image_4),
      image_5: normalizeStringField(customer.image_5),
      role,
      salonName: normalizeStringField(customer.salon_name),
      address: normalizeStringField(customer.address),
      business: customer.business === true,
      cutting: customer.cutting === true,
      color: customer.color === true,
      technique: customer.technique === true,
      isSalonOwner: isCertifiedSalon,
      isCertifiedSalon,
      elevateMember: customer.elevate_member === true,
      showOnMap: customer.show_on_map === true,
      tags,
    }
  }

  function mapSearchCustomer(customer) {
    const stylistRecord = mapCustomerBase(customer)
    if (!stylistRecord.name) {
      stylistRecord.name = 'Unnamed Stylist'
    }

    stylistRecord.salon_name = stylistRecord.salonName
    stylistRecord.business_name = stylistRecord.salonName
    stylistRecord.shopify_id = customer.shopify_id
    stylistRecord.tags = stylistRecord.tags || []

    return withRoleCategory(stylistRecord)
  }

  // ============================================================================
  // API Functions
  // ============================================================================

  /**
   * Load stylists from the API within the given bounds.
   * @param {string} apiEndpoint - API endpoint URL
   * @param {Object} bounds - Geographic bounds { latitude_low, latitude_high, longitude_low, longitude_high }
   * @param {Object} toastManager - Toast manager for UI feedback
   * @returns {Promise<Array>} Array of stylist records
   */
  async function loadStylists(apiEndpoint, bounds, toastManager) {
    if (!apiEndpoint) return []

    const loadingTimeout = setTimeout(() => {
      toastManager?.show('loading', {
        message: 'Loading...',
        severity: 'info',
        duration: Infinity,
      })
    }, 200)

    try {
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bounds),
      })

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
      }

      const data = await response.json()
      const customers = Array.isArray(data.customers) ? data.customers : []
      const mappedStylists = customers.map(mapCustomerToStylist)

      clearTimeout(loadingTimeout)
      toastManager?.remove('loading')
      return mappedStylists
    } catch (error) {
      console.error('Stylist Locator:', error)
      clearTimeout(loadingTimeout)
      toastManager?.show('loading', {
        message: 'Failed to load stylists. Please try again.',
        severity: 'error',
        duration: 4000,
      })
      return []
    }
  }

  /**
   * Map a customer API response to a stylist record.
   * @param {Object} customer - Raw customer data from API
   * @returns {Object} Normalized stylist record
   */
  function mapCustomerToStylist(customer) {
    return withRoleCategory(mapCustomerBase(customer))
  }

  /**
   * Search for stylists by query string.
   * @param {string} apiEndpoint - API endpoint URL
   * @param {string} query - Search query
   * @param {Array} cachedStylists - Cached stylists for fallback local search
   * @param {AbortSignal} signal - Optional AbortSignal for request cancellation
   * @returns {Promise<Array>} Array of matching stylists (max 3)
   */
  async function searchStylists(apiEndpoint, query, cachedStylists = [], signal = null) {
    if (!apiEndpoint || !query || query.trim().length < MIN_SEARCH_LENGTH) {
      return []
    }

    try {
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ search: query.trim() }),
        signal,
      })

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
      }

      const data = await response.json()
      const customers = Array.isArray(data.customers) ? data.customers : []

      const mappedStylists = customers.map(mapSearchCustomer)
      const visibleResults = mappedStylists.filter(shouldIncludeInSearchResult)

      return visibleResults.slice(0, AUTOCOMPLETE_RESULT_LIMIT)
    } catch (error) {
      if (error.name === 'AbortError') throw error
      console.error('Error searching stylists:', error)
      return performLocalSearch(query, cachedStylists)
    }
  }

  /**
   * Perform a local search on cached stylists as fallback.
   * @param {string} query - Search query
   * @param {Array} cachedStylists - Cached stylists to search
   * @returns {Array} Matching stylists (max 3)
   */
  function performLocalSearch(query, cachedStylists) {
    const searchTerm = query.toLowerCase()

    const matches = cachedStylists.filter((stylist) => {
      const name = (stylist.name || '').toLowerCase()
      const salon = (stylist.salon_name || stylist.business_name || stylist.salonName || '').toLowerCase()
      const role = (stylist.role || '').toLowerCase()
      return name.includes(searchTerm) || salon.includes(searchTerm) || role.includes(searchTerm)
    })

    return matches.slice(0, AUTOCOMPLETE_RESULT_LIMIT)
  }

  // ============================================================================
  // Directory API Functions
  // ============================================================================

  /**
   * Fetch stylists for the complete directory with pagination support.
   * @param {string} apiBaseDomain - API base domain URL
   * @param {number} page - Page number (1-indexed)
   * @param {number} limit - Number of items per page
   * @param {string} search - Optional search query
   * @returns {Promise<Object>} Object containing stylists array and pagination info
   */
  async function fetchDirectoryStylists(apiBaseDomain, page = 1, limit = 50, search = '') {
    if (!apiBaseDomain) {
      console.warn('fetchDirectoryStylists: API base domain not provided')
      return { stylists: [], pagination: { page: 1, total_pages: 1, total_count: 0 } }
    }

    const base = apiBaseDomain.replace(/\/$/, '')
    const url = new URL(`${base}/api/stylists`)
    url.searchParams.set('page', page.toString())
    url.searchParams.set('limit', limit.toString())
    if (search && search.trim()) {
      url.searchParams.set('search', search.trim())
    }

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
      })

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
      }

      const data = await response.json()

      return {
        stylists: Array.isArray(data.stylists) ? data.stylists : [],
        pagination: data.pagination || {
          page: 1,
          limit: limit,
          total_count: 0,
          total_pages: 1,
          has_next_page: false,
          has_previous_page: false,
        },
      }
    } catch (error) {
      console.error('fetchDirectoryStylists: Failed to fetch', error)
      return {
        stylists: [],
        pagination: {
          page: 1,
          limit: limit,
          total_count: 0,
          total_pages: 1,
          has_next_page: false,
          has_previous_page: false,
        },
      }
    }
  }

  // ============================================================================
  // Exports
  // ============================================================================

  namespace.api = Object.assign({}, namespace.api, {
    loadStylists,
    searchStylists,
    fetchDirectoryStylists,
  })
})()
